import React, { useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getLibraryNavPath } from "../../utils/libraryNav";
import {
  DRIVE_OPEN_UPLOAD_MODAL_STATE,
  DRIVE_PATH,
} from "../../utils/driveNavigation";

const DriveHomeCta: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const driveColor = theme.palette.primary.main;
  const worksheetsIconColor = theme.palette.warning.main;
  const worksheetsWellBg = theme.palette.cards.orangeBg;
  const labelFont = theme.typography.pxToRem(14);

  const isSchoolOrg = useMemo(() => {
    const t =
      typeof user?.schoolType === "string"
        ? user.schoolType.toLowerCase().trim()
        : "";
    return t === "school";
  }, [user?.schoolType]);

  const title = isSchoolOrg ? "Library" : "Add to Drive";
  const subtitle = isSchoolOrg
    ? "Worksheets and learning content for your organization."
    : "Upload folders and files for your circles.";
  const go = () => {
    if (isSchoolOrg) {
      navigate(getLibraryNavPath(user));
      return;
    }
    navigate(DRIVE_PATH, { state: DRIVE_OPEN_UPLOAD_MODAL_STATE });
  };

  return (
    <Paper
      elevation={0}
      onClick={go}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          go();
        }
      }}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        p: compact ? 1 : { xs: 1.5, md: 2 },
        display: "flex",
        alignItems: "center",
        minHeight: compact ? 56 : { xs: 70, md: 80 },
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
          boxShadow: 1,
          borderColor: "primary.main",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 1.5 },
          width: "100%",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 1.5 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: compact ? 30 : { xs: theme.spacing(5), md: theme.spacing(6) },
              height: compact ? 30 : { xs: theme.spacing(5), md: theme.spacing(6) },
              bgcolor: isSchoolOrg ? worksheetsWellBg : alpha(driveColor, 0.1),
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isSchoolOrg ? (
              <DescriptionOutlined
                sx={{
                  width: { xs: theme.spacing(2.5), md: theme.spacing(3) },
                  height: { xs: theme.spacing(2.5), md: theme.spacing(3) },
                  color: worksheetsIconColor,
                }}
              />
            ) : (
              <FolderIcon
                sx={{
                  width: { xs: theme.spacing(2.5), md: theme.spacing(3) },
                  height: { xs: theme.spacing(2.5), md: theme.spacing(3) },
                  color: driveColor,
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: {
                  xs: theme.typography.pxToRem(12),
                  md: labelFont,
                },
                fontWeight: 500,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[700]
                    : theme.palette.grey[600],
                lineHeight: 1.2,
                mb: 0.25,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: {
                  xs: theme.typography.pxToRem(11),
                  md: theme.typography.pxToRem(12),
                },
                color: "text.secondary",
                lineHeight: 1.35,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default DriveHomeCta;
